import { useTranslation } from "react-i18next";
import { ProfileSectionProps } from "./types";
import { FormSection } from "@/features/common/components/FormSection";
import { LanguageLevelFields } from "./LanguageSkills/LanguageLevelFields";
import { LanguageCertificateField } from "./LanguageSkills/LanguageCertificateField";
import {
  useFileUploader,
  DOCUMENT_TYPES,
  Document
} from "@/components/features/FileUploader";
import { useDocumentTypes } from "@/hooks/useDocumentTypes";
import { useState, useEffect, useCallback } from "react";
import { DataField } from "@/features/common/components/DataField";
import { FileIcon } from "lucide-react";
import { Button } from "@/components/ui/button";

// Label mapping functions with internationalization
const getGermanLevelLabel = (value: string, t: any) => {
  const key = value.toLowerCase();
  return t(`forms.sections.language_skills.levels.${key}`, value);
};

const getLanguageSchoolLabel = (value: string, t: any) => {
  return t(`forms.sections.language_skills.schools.${value}`, value);
};

export function LanguageSkillsSection({
  profile,
  setProfile,
  loading,
  readOnly
}: ProfileSectionProps) {
  const { t } = useTranslation(["talent", "common"]);

  return (
    <FormSection title={t("common:german_skills")}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <LanguageCertificateField
          profile={profile}
          readOnly={readOnly}
          loading={loading}
        />
        <LanguageLevelFields
          profile={profile}
          setProfile={setProfile}
          readOnly={readOnly}
          loading={loading}
        />
      </div>
    </FormSection>
  );
}

// View component
export function LanguageSkillsView({ profile }: { profile: any }) {
  const { t } = useTranslation(["talent"]);
  const { handleViewFile, getTalentDocument } = useFileUploader();
  const { data: documentTypes = [] } = useDocumentTypes();
  const [germanLanguageCertificateFile, setGermanLanguageCertificateFile] =
    useState<Document | undefined>(undefined);
  const [loadingDocumentRead, setLoadingDocumentRead] = useState(false);

  const fetchGermanLanguageCertificateFile = useCallback(() => {
    const docType = documentTypes.find(
      (d) => d.id === DOCUMENT_TYPES.GERMAN_LANGUAGE_CERTIFICATE.id
    );
    if (!docType) return;

    setLoadingDocumentRead(true);
    getTalentDocument(profile.talent_id || profile.id, docType).then(
      (res: any) => {
        if (res.success) {
          setGermanLanguageCertificateFile(res.data[0]);
        }
        setLoadingDocumentRead(false);
      }
    );
  }, [profile.talent_id, profile.id, getTalentDocument, documentTypes]);

  useEffect(() => {
    if ((profile?.talent_id || profile?.id) && documentTypes.length > 0) {
      fetchGermanLanguageCertificateFile();
    }
  }, [
    profile?.talent_id,
    profile?.id,
    fetchGermanLanguageCertificateFile,
    documentTypes
  ]);

  return (
    <div className="space-y-1">
      <DataField
        label={t("forms.sections.language_skills.view_labels.german_skills")}
        value={
          profile.german_language_skills
            ? getGermanLevelLabel(profile.german_language_skills, t)
            : null
        }
      />
      <DataField
        label={t("forms.sections.language_skills.view_labels.language_school")}
        value={
          profile.language_school
            ? getLanguageSchoolLabel(profile.language_school, t)
            : null
        }
      />
      {!loadingDocumentRead && (
        <DataField
          label={t(
            "forms.sections.language_skills.view_labels.german_certificate"
          )}
          value={
            germanLanguageCertificateFile ? (
              <Button
                variant="link"
                className="p-0 h-auto text-blue-600"
                onClick={() =>
                  handleViewFile(
                    "profiles",
                    germanLanguageCertificateFile.file_path
                  )
                }
              >
                <FileIcon className="w-4 h-4 mr-1" />
                {t("forms.sections.language_skills.buttons.view_document")}
              </Button>
            ) : null
          }
        />
      )}
    </div>
  );
}
